import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Tag, Theme, ToastNotification } from '@carbon/react';
import {
  Add,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Notebook,
  TrashCan,
  User,
} from '@carbon/icons-react';
import axiosInstance from '../../utils/axiosInstance';
import Collapsible from '../Collapsible';
import ShiftNoteFormDrawer from './ShiftNoteFormDrawer';
import authService from '../../services/authService';
import { useTheme } from '../../contexts/ThemeContext';
import {
  NOTES_PAGE_SIZE,
  formatDateTime,
  normalizePagination,
  pluralForm,
  severityLabel,
  severityTagType,
} from './workDayUtils';

// Normalizuje odgovor GET /api/ShiftNote/shift/{shiftId} ({ success, data: [...] })
// u listu beleški sortiranu od najnovije ka najstarijoj.
const normalizeNotes = (body) => {
  const payload = body && body.data !== undefined ? body.data : body;
  const list = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.notes)
    ? payload.notes
    : [];
  return list
    .filter((note) => note && (note.id || note.note))
    .slice()
    .sort(
      (a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
    );
};

// Beleška se može dodati samo dok smena traje: mora da je već počela
// (startTime <= now) i da nije završena (endTime > now).
const isShiftOngoing = (shift) => {
  if (!shift) return false;
  const now = Date.now();
  const start = shift.startTime ? new Date(shift.startTime).getTime() : null;
  const end = shift.endTime ? new Date(shift.endTime).getTime() : null;
  if (start != null && !Number.isNaN(start) && now < start) return false;
  if (end != null && !Number.isNaN(end) && now >= end) return false;
  return true;
};

/**
 * ShiftNotesPanel - "Beleške smene": prikazuje se unutar trake sa trenutnom
 * smenom ("U smeni"), kao njen donji deo.
 *
 * Lista se učitava za OBLAST za tekući dan
 * (GET /api/ShiftNote/area-today/{areaId}?pageNumber=&pageSize=5) - isto kao na
 * admin panelu. Nova beleška se dodaje preko forme u desnom panelu ("+" dugme)
 * koja šalje POST /api/ShiftNote ({ shiftId, note, severity, residentId }) - i to
 * samo dok smena traje.
 * Belešku može obrisati njen autor ili administrator (DELETE /api/ShiftNote/{id}),
 * uz potvrdu u modalu (isto kao za ostale entitete).
 */
const ShiftNotesPanel = ({ shift = null, onOpenResident = null }) => {
  const { theme } = useTheme();
  const shiftId = shift?.id ?? null; // za dodavanje beleške (POST /api/ShiftNote)
  const areaId = shift?.areaId ?? null; // za listu (GET /api/ShiftNote/area-today/{areaId})

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [pagination, setPagination] = useState(() => normalizePagination(null));
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [toast, setToast] = useState(null);

  const canAddNote = useMemo(() => isShiftOngoing(shift), [shift]);

  // Ko sme da obriše belešku: njen autor ili administrator
  // (endpoint: DELETE /api/ShiftNote/{id}).
  const { currentEmployeeId, isAdmin } = useMemo(() => {
    const currentUser = authService.getCurrentUser();
    return {
      currentEmployeeId: currentUser?.employee_id || null,
      isAdmin: authService.getUserRole(currentUser) === 'administrator',
    };
  }, []);

  const showToast = useCallback((kind, title, subtitle) => {
    setToast({ kind, title, subtitle });
    window.setTimeout(() => setToast(null), 4000);
  }, []);

  const fetchNotes = useCallback(
    async (targetPage = 1) => {
      // Lista beleški se čita za OBLAST za tekući dan (isto kao na admin panelu).
      // Fallback na smenu ako smena nema dodeljenu oblast.
      const endpoint = areaId
        ? `/api/ShiftNote/area-today/${areaId}`
        : shiftId
        ? `/api/ShiftNote/shift/${shiftId}`
        : null;
      if (!endpoint) return;

      setLoading(true);
      setLoadError(null);
      try {
        const response = await axiosInstance.get(endpoint, {
          params: { pageNumber: targetPage, pageSize: NOTES_PAGE_SIZE },
        });
        const list = normalizeNotes(response.data);
        setNotes(list);
        setPagination(normalizePagination(response.data, list.length, NOTES_PAGE_SIZE));
      } catch (err) {
        console.error('Greška pri učitavanju beleški smene:', err);
        setNotes([]);
        setPagination(normalizePagination(null, 0, NOTES_PAGE_SIZE));
        setLoadError('Grešku pri učitavanju beleški smene.');
      } finally {
        setLoading(false);
      }
    },
    [areaId, shiftId]
  );

  // Učitaj beleške za trenutnu smenu (i ponovo kad se smena promeni)
  useEffect(() => {
    if (shiftId == null && areaId == null) {
      setNotes([]);
      setLoadError(null);
      setIsFormOpen(false);
      setPagination(normalizePagination(null, 0, NOTES_PAGE_SIZE));
      return undefined;
    }
    setIsFormOpen(false);
    fetchNotes(1);
    return undefined;
  }, [shiftId, areaId, fetchNotes]);

  // Posle uspešnog dodavanja: osveži listu (nova beleška je najnovija -> strana 1)
  const handleNoteSaved = useCallback(() => {
    fetchNotes(1);
  }, [fetchNotes]);

  const handlePageChange = useCallback(
    (nextPage) => {
      if (nextPage < 1 || nextPage === pagination.pageNumber) return;
      fetchNotes(nextPage);
    },
    [fetchNotes, pagination.pageNumber]
  );

  const closeForm = useCallback(() => setIsFormOpen(false), []);

  // Nema trenutne smene - nema ni beleški smene
  if (shiftId == null && areaId == null) return null;

  // Belešku može obrisati njen autor ili administrator
  const canDeleteNote = (note) =>
    Boolean(
      note?.id &&
        (isAdmin ||
          (currentEmployeeId &&
            note.employeeId &&
            String(note.employeeId).toLowerCase() === String(currentEmployeeId).toLowerCase()))
    );

  const handleDeleteNote = async (note) => {
    if (!note?.id || isDeleting) return;
    setIsDeleting(true);
    setActionError(null);
    try {
      await axiosInstance.delete(`/api/ShiftNote/${note.id}`);
      // Ako je obrisana poslednja beleška na strani, vrati se na prethodnu stranu
      const remaining = Math.max(0, (pagination.totalCount || notes.length) - 1);
      const lastPage = Math.max(1, Math.ceil(remaining / NOTES_PAGE_SIZE));
      const targetPage = Math.min(pagination.pageNumber || 1, lastPage);

      showToast('success', 'Uspešno obrisano', 'Beleška smene je uspešno obrisana.');
      await fetchNotes(targetPage);
    } catch (err) {
      console.error('Greška pri brisanju beleške smene:', err);
      const message =
        err?.response?.data?.message || 'Belešku nije moguće obrisati. Pokušajte ponovo.';
      setActionError(message);
      showToast('error', 'Greška', message);
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
      setNoteToDelete(null);
    }
  };

  // Otvori modal za potvrdu brisanja (isto kao za ostale entitete)
  const openDeleteModal = (note) => {
    setNoteToDelete(note);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setNoteToDelete(null);
  };

  // Broj beleški u zaglavlju = UKUPAN broj (iz paginacije), a ne samo broj
  // beleški na trenutnoj strani.
  const totalCount = pagination.totalCount ?? notes.length;
  const countLabel =
    totalCount === 1
      ? '1 beleška'
      : `${totalCount} ${pluralForm(totalCount, 'beleška', 'beleške', 'beleški')}`;

  return (
    <div className="wdash-notes">
      <div className="wdash-notes__head">
        <button
          type="button"
          className="wdash-notes__toggle"
          onClick={() => setIsCollapsed((prev) => !prev)}
          aria-expanded={!isCollapsed}
        >
          <span className="wdash-notes__head-icon">
            <Notebook size={20} />
          </span>
          <span className="wdash-notes__head-text">
            <span className="wdash-notes__title">Beleške smene</span>
          </span>
          <span className="wdash-notes__count">{countLabel}</span>
          <ChevronDown
            size={20}
            className={`wdash-notes__chevron ${isCollapsed ? '' : 'is-expanded'}`}
          />
        </button>

        {/* Dodavanje beleške (forma je u desnom panelu) - samo dok smena traje */}
        {canAddNote && (
          <button
            type="button"
            className="wdash-notes__add"
            onClick={() => setIsFormOpen(true)}
            aria-label="Nova beleška"
            title="Nova beleška"
          >
            <Add size={20} />
          </button>
        )}
      </div>

      <Collapsible open={!isCollapsed}>
        <div className="wdash-notes__body">
          {!canAddNote && (
            <p className="wdash-note__info">Belešku je moguće dodati samo dok smena traje.</p>
          )}

          {actionError && <p className="wdash-note__error">{actionError}</p>}

          {loading ? (
            <div className="wdash-note__skeleton">
              <span />
              <span />
            </div>
          ) : loadError ? (
            <p className="wdash-note__error">{loadError}</p>
          ) : notes.length === 0 ? (
            <p className="wdash-note__empty">Nema beleški za ovu smenu.</p>
          ) : (
            <>
              <ul className="wdash-note__list">
              {notes.map((note) => {
                const sevLabel = severityLabel(note.severity);
                return (
                  <li key={note.id} className="wdash-note__item">
                    <div className="wdash-note__meta">
                      <span className="wdash-note__author">
                        {note.employeeName || 'Nepoznat autor'}
                      </span>
                      <span className="wdash-note__time">{formatDateTime(note.createdAt)}</span>
                    </div>

                    {(sevLabel || note.residentName || canDeleteNote(note)) && (
                      <div className="wdash-note__tags">
                        {sevLabel && (
                          <Tag size="sm" type={severityTagType(note.severity)}>
                            {sevLabel}
                          </Tag>
                        )}

                        {note.residentName &&
                          (onOpenResident && note.residentId ? (
                            <button
                              type="button"
                              className="wdash-note__resident"
                              onClick={() => onOpenResident(note.residentId)}
                              title={`Otvori profil: ${note.residentName}`}
                            >
                              <User size={12} />
                              {note.residentName}
                            </button>
                          ) : (
                            <span className="wdash-note__resident">
                              <User size={12} />
                              {note.residentName}
                            </span>
                          ))}

                        {canDeleteNote(note) && (
                          <button
                            type="button"
                            className="wdash-note__delete"
                            onClick={() => openDeleteModal(note)}
                            aria-label="Obriši belešku"
                            title="Obriši belešku"
                          >
                            <TrashCan size={14} />
                          </button>
                        )}
                      </div>
                    )}

                    <p className="wdash-note__text">{note.note}</p>
                  </li>
                );
              })}
              </ul>

              {pagination.totalPages > 1 && (
                <div className="wdash-note__pagination">
                  <button
                    type="button"
                    className="wdash-note__pagination-btn"
                    onClick={() => handlePageChange(pagination.pageNumber - 1)}
                    disabled={!pagination.hasPreviousPage || loading}
                    aria-label="Prethodna strana"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <span className="wdash-note__pagination-info">
                    {pagination.pageNumber} / {pagination.totalPages}
                  </span>
                  <button
                    type="button"
                    className="wdash-note__pagination-btn"
                    onClick={() => handlePageChange(pagination.pageNumber + 1)}
                    disabled={!pagination.hasNextPage || loading}
                    aria-label="Sledeća strana"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </Collapsible>

      {/* Forma za novu belešku u desnom panelu (portal na body) */}
      <ShiftNoteFormDrawer
        shift={isFormOpen ? shift : null}
        onClose={closeForm}
        onSaved={handleNoteSaved}
      />

      {/* Notifikacija (toast) - npr. posle brisanja beleške */}
      {toast && (
        <div className="wdash-toast">
          <ToastNotification
            kind={toast.kind}
            title={toast.title}
            subtitle={toast.subtitle}
            lowContrast
            timeout={0}
            onClose={() => setToast(null)}
          />
        </div>
      )}

      {/* Potvrda brisanja beleške - modal (kao i za ostale entitete) */}
      <Theme theme={theme}>
        <Modal
          open={isDeleteModalOpen}
          onRequestClose={closeDeleteModal}
          modalHeading="Brisanje beleške smene"
          primaryButtonText={isDeleting ? 'Brisanje...' : 'Obriši'}
          secondaryButtonText="Otkaži"
          onRequestSubmit={() => handleDeleteNote(noteToDelete)}
          danger
          size="sm"
        >
          <p>Da li ste sigurni da želite da obrišete ovu belešku smene?</p>
          <p style={{ marginTop: '1rem', fontSize: '0.875rem', opacity: 0.7 }}>
            Ova akcija se ne može poništiti.
          </p>
        </Modal>
      </Theme>
    </div>
  );
};

export default ShiftNotesPanel;
